// ABOUTME: The stack of four sculpted clouds and eight bridge lobes in the clouds scene, drawn from the crossing's stored readings.
// ABOUTME: A screen supplies only its own live, unsent tier; each cloud takes its color from the picked feeling once its answer is given.

import { useEffect, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { Box } from '@mui/material';
import { useCrossingContent } from '../../../hooks/useCrossingContent';
import { RepairStatus, type CrossingWithProgress } from '../../../types/api';
import { useHorizon } from '../crossing/useHorizon';
import { QuestionArrivalContext } from '../crossing/questionArrival';
import { useCrossingReducedMotion } from '../crossing/useReducedMotion';
import { useFrameScale } from '../crossing/useFrameScale';
import { useShellBottom } from '../crossing/useShellBottom';
import { PLACEHOLDER_PALETTE, paletteFor } from '../screens/clouds/cloudPalette';
import type { PaletteRole } from '../screens/clouds/stackGeometry';
import { LOBES, STACK_CANVAS, STACK_ROOT, STAGE, STAGE_HORIZON, TIERS, toStage } from '../screens/clouds/stackGeometry';
import type { Tier, TierId } from '../screens/clouds/stackGeometry';
import { revealFor } from '../screens/clouds/reveal';
import SculptedCloud from '../screens/clouds/SculptedCloud';
import StormCrown, { STORM_CROWN_SCALE } from '../screens/clouds/StormCrown';
import TierArrows from '../screens/clouds/TierArrows';
import WeatherVignette from '../screens/clouds/WeatherVignette';
import { vignetteFor } from '../screens/clouds/weatherVignettes';
import { FeelingCopy, RevealCopy, StormCopy, WeatherCopy } from '../screens/clouds/tierCopy';
import { FeelingBlob, RevealBlob, StormBlob, WeatherBlob } from '../screens/clouds/tierBlobs';
import { DIMMED_OPACITY, settleSx, settleStyle } from '../screens/clouds/settleFade';
import { BUILD_SCREENS, LAST_PUFF_ID, LOBE_BUILD, TIER_BUILD, stackBuildStyle, stackBuildSx } from '../screens/clouds/stackBuild';
import ImpactRain from '../screens/impact/ImpactRain';
import { impactPx } from '../screens/impact/impactScale';
import { LIGHTEN_SEC, useStormDissolve } from '../screens/impact/stormDissolve';
import type { ScreenResult } from './screenContract';
import { desktopReachUnit, desktopStackScale, phoneStackScale } from './stackScale';
import { useStackPlateTop } from './useStackPlateTop';
import { useViewportWidth } from './useViewportWidth';

interface CloudStackProps {
  crossing: CrossingWithProgress;
  cloudTiers?: ScreenResult['cloudTiers'];
  buildIn?: boolean; // the stack builds bottom-up (2B arriving from 2A by This feels close)
}

const REVEAL_SCREEN = '2C';
// On this screen the eyebrow of the crown copy has its own fade, which plays on each arrival.
const WEATHER_SCREEN = '2D';
// On this screen the whole stack steps back and the question cloud is drawn at once over the third tier; its lines fade in on an arrival.
const QUESTION_SCREEN = '5B';
const QUESTION_Z = 6;
// The weather vignette draws above the four clouds (z 2 to 5) and below the tier arrows (z 7).
const VIGNETTE_Z = 6;
// On these screens a storm-grey copy of the crown tier is drawn over the lit crown as the head of the rain, and the tiers
// below the crown fade back as the rain starts. The reach tiles and the rain are laid out in the page's flow over the stack's canvas. 4A and 4B draw the same
// scene finished, with nothing left to play; 4B's dissolves when forgiven.
const IMPACT_SCREENS = new Set(['3A', '4A', '4B']);
const ENTRANCE_SCREEN = '3A';
const FORGIVE_SCREEN = '4B';

// Which tier's answer colors each bridge lobe: a lobe is drawn once the tier it leads into is revealed.
const LOBE_TIER: Record<PaletteRole, TierId> = {
  base: 'base', lower: 'lower', upper: 'upper', head: 'head',
  b1: 'base', b2: 'lower', b3l: 'upper', b3r: 'upper', b4l: 'head', b4r: 'head',
};

const tierOf = (id: TierId): Tier => TIERS.find((t) => t.id === id)!;
const centerY = (id: TierId) => tierOf(id).box[1] + tierOf(id).box[3] / 2;

export default function CloudStack({ crossing, cloudTiers, buildIn }: CloudStackProps) {
  const { data: content } = useCrossingContent();
  const reducedMotion = useCrossingReducedMotion();
  const { frameSc: phoneSc } = useFrameScale(STAGE);
  const { frameH, line, phone } = useHorizon();
  const viewportWidth = useViewportWidth();
  const shellBottom = useShellBottom();
  const plateTop = useStackPlateTop();
  // Both draw the stack at twice their base size, held smaller where it would not fit. The phone's base is the stage fitted to
  // the width; the desktop's is v28's size. The reach tiles keep the base size.
  const room = { width: viewportWidth, height: frameH, line, shellBottom, plateTop };
  const frameSc = phone ? phoneStackScale({ phoneSc, ...room }) : desktopStackScale(room);
  // The rain falls under the storm crown only: as wide as the crown is drawn, about the crown's centre.
  const [headX, , headW] = tierOf('head').box;
  const crownSpan = {
    width: headW * STORM_CROWN_SCALE * STACK_ROOT.scale * frameSc,
    centre: (toStage(headX + headW / 2, 0)[0] - STAGE / 2) * frameSc,
  };
  const reachSc = phone ? phoneSc : desktopStackScale(room, 1);
  const { progress } = crossing;

  // The first paint of the stored state draws as it is (a reload mid-flow must not replay the reveal); later changes
  // animate. The stored state only exists once the content has loaded, so settling waits for it.
  const contentLoaded = !!content;
  const [settled, setSettled] = useState(false);
  useEffect(() => {
    if (!contentLoaded) return undefined;
    const frame = requestAnimationFrame(() => setSettled(true));
    return () => cancelAnimationFrame(frame);
  }, [contentLoaded]);

  // The build flag is read once, at mount, so a build that has started is neither restarted nor cut by 2C, 2D, arrowing
  // or Back from 2C. It ends when the child leaves the build's screens or the last puff's animation ends.
  const [buildPlaying, setBuildPlaying] = useState(!!buildIn);
  if (buildPlaying && !BUILD_SCREENS.has(progress.screenId)) setBuildPlaying(false);
  const build = buildPlaying && !reducedMotion;

  // Only a change of screen sets the eyebrow's fade: a first paint (a reload) and a rerender on the screen (browsing) leave
  // it as it is, and reduced motion never plays it.
  const [seenScreen, setSeenScreen] = useState(progress.screenId);
  const [eyebrowFade, setEyebrowFade] = useState(false);
  // The storm crown fades in only on the arrival at 3A from 2D; 4A to 3A, a reload and reduced motion draw it as it is.
  const [stormCrownFade, setStormCrownFade] = useState(false);
  if (seenScreen !== progress.screenId) {
    setSeenScreen(progress.screenId);
    setStormCrownFade(progress.screenId === ENTRANCE_SCREEN && seenScreen === WEATHER_SCREEN && settled && !reducedMotion);
    setEyebrowFade(progress.screenId === WEATHER_SCREEN && settled && !reducedMotion);
  }

  const feeling = crossing.feeling ? content?.feelings?.[crossing.feeling] : undefined;
  const storedStorm = crossing.storm ? content?.storms?.[crossing.storm] : undefined;
  const storedWeather = crossing.weather ? content?.weatherPatterns?.[crossing.weather] : undefined;
  const liveStorm = cloudTiers?.storm;
  const liveWeather = cloudTiers?.weather;
  const storm = liveStorm ? liveStorm.item : storedStorm;
  const weather = liveWeather ? liveWeather.item : storedWeather;

  // The reveal shows once the child has reached 2C, and goes empty again if they step back to 2B.
  const reachedReveal = progress.path.slice(0, progress.pathIndex + 1).includes(REVEAL_SCREEN);
  const reveal = reachedReveal ? revealFor(storedStorm) : undefined;
  const dimmed = progress.screenId === QUESTION_SCREEN;

  const palette = crossing.feeling ? paletteFor(crossing.feeling) : PLACEHOLDER_PALETTE;
  const revealed: Record<TierId, boolean> = { base: !!feeling, lower: !!storm, upper: !!reveal, head: !!weather };
  const fillOf = (role: PaletteRole) => (revealed[LOBE_TIER[role]] ? palette[role] : PLACEHOLDER_PALETTE[role]);

  // The entrance: the lower three tiers fade back, and the weather response is read once, from the head.
  const impact = IMPACT_SCREENS.has(progress.screenId);
  // Forgiven on 4B: the storm leaves (the rain and the storm crown fade out) and the stack comes up to its lit colors.
  const forgiven = progress.screenId === FORGIVE_SCREEN && crossing.repairStatus === RepairStatus.CompleteForgiven;
  const dissolve = useStormDissolve(forgiven, progress.screenId);
  const stormy = impact && dissolve.present;
  // Only a press played on this screen lightens by transition; arriving forgiven draws the lit state at once.
  const litTransition = dissolve.pressed && settled && !reducedMotion ? `opacity ${LIGHTEN_SEC}s ease` : 'none';
  // Off the entrance's own screen the fades have already played: everything sits where they end.
  const still = impact && progress.screenId !== ENTRANCE_SCREEN;
  const calm = reducedMotion || still;
  // The bridge lobes fade with the tier they lead into; the lobes into the crown stay as they are.
  const lobeFade = (role: PaletteRole) => {
    if (!impact) return {};
    if (forgiven) return { sx: { opacity: 1, transition: litTransition } };
    return LOBE_TIER[role] === 'head'
      ? {}
      : { sx: settleSx(calm, false), style: settleStyle(calm) };
  };

  const instant = !settled;
  const lobeTransition = reducedMotion || instant ? 'none' : 'background 1.1s ease, opacity 1s ease';
  const cloud = (id: TierId, testId: string, children: ReactNode) => (
    <SculptedCloud
      key={id}
      tier={tierOf(id)}
      fill={fillOf(id)}
      testId={testId}
      instant={instant}
      dimmed={dimmed}
      settled={impact && id !== 'head'}
      lit={forgiven}
      litTransition={litTransition}
      still={still}
      keepWords={id === 'base'}
      build={build ? TIER_BUILD[id] : undefined}
    >
      {children}
    </SculptedCloud>
  );

  return (
    <>
    {/* The stage is anchored to the viewport like the sea's horizon line it stands on; pointerEvents none keeps it from
        covering a screen's own controls, and the tier arrows turn it back on. */}
    <Box
      data-testid="cloud-stack"
      data-stack-scale={frameSc}
      sx={{
        position: 'fixed', left: '50%', top: `${line - STAGE_HORIZON * frameSc}px`, width: STAGE, height: STAGE,
        marginLeft: `${-STAGE / 2}px`, transform: `scale(${frameSc})`, transformOrigin: '50% 0', zIndex: 1, pointerEvents: 'none',
      }}
    >
      <Box
        sx={{
          position: 'absolute', left: STACK_ROOT.left, top: 0, width: STACK_ROOT.size, height: STACK_ROOT.size,
          transform: `translateY(${-STACK_ROOT.lift}px) scale(${STACK_ROOT.scale})`, transformOrigin: '50% 0',
        }}
      >
        <Box sx={{ position: 'absolute', left: STACK_CANVAS.rootLeft, top: STACK_CANVAS.rootTop, width: STACK_CANVAS.width, height: STACK_CANVAS.height }}>
          {LOBES.map((lobe) => {
            const fade = lobeFade(lobe.role);
            const lobeBuild = build ? LOBE_BUILD[lobe.id] : undefined;
            return (
              <Box
                key={lobe.id}
                data-lobe={lobe.id}
                aria-hidden="true"
                style={lobeBuild ? stackBuildStyle(lobeBuild) : fade.style}
                onAnimationEnd={lobe.id === LAST_PUFF_ID && build ? () => setBuildPlaying(false) : undefined}
                sx={{
                  position: 'absolute', left: lobe.x, top: lobe.y, width: lobe.w, height: lobe.h, borderRadius: '50%',
                  backgroundColor: fillOf(lobe.role), opacity: dimmed ? DIMMED_OPACITY : 1, transition: lobeTransition,
                  ...(lobeBuild ? stackBuildSx(lobeBuild) : {}), ...fade.sx,
                }}
              />
            );
          })}

          {cloud('head', 'storm-weather-cloud', weather && <WeatherCopy weather={weather} reveal={settled && !build} eyebrowFade={eyebrowFade} eyebrowFull={progress.screenId === WEATHER_SCREEN} showName={!(liveWeather && weather && !impact && vignetteFor(weather.code))} />)}
          {cloud('upper', 'reveal-cloud', reveal && <RevealCopy heading={reveal.heading} text={reveal.text} reveal={settled && !build} />)}
          {cloud('lower', 'storm-tier', storm && <StormCopy storm={storm} reveal={settled && !build} />)}
          {cloud('base', 'storm-feeling-cloud', feeling && <FeelingCopy feeling={feeling} reveal={settled && !build} />)}
          {stormy && <StormCrown weather={weather} fadeIn={stormCrownFade} dissolving={dissolve.fading} />}

          {cloudTiers?.question && (
            <Box
              sx={{
                position: 'absolute', left: tierOf('upper').box[0], top: tierOf('upper').box[1], width: tierOf('upper').box[2], height: tierOf('upper').box[3],
                display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: QUESTION_Z, pointerEvents: 'none',
              }}
            >
              {/* The question reads this once at mount: a first paint (settled is false on a reload, or on a fresh stack after Back from 5C)
                  draws at rest, and a mounted stack plays the stagger. */}
              <QuestionArrivalContext.Provider value={settled}>{cloudTiers.question}</QuestionArrivalContext.Provider>
            </Box>
          )}

          {/* The focused weather's vignette plays over the head, a sibling of it so it never takes on the head's fade or dim.
              Only the live, browsable weather tier has one: the stored weather on 3A and later does not. */}
          {liveWeather && weather && !impact && (
            <Box
              aria-hidden="true"
              sx={{
                position: 'absolute', left: tierOf('head').box[0], top: tierOf('head').box[1], width: tierOf('head').box[2], height: tierOf('head').box[3],
                zIndex: VIGNETTE_Z, pointerEvents: 'none', overflow: 'visible',
              }}
            >
              <WeatherVignette code={weather.code} name={weather.name} />
            </Box>
          )}

          {liveWeather?.onPrev && liveWeather.onNext && (
            <TierArrows
              centerY={centerY('head')}
              onPrev={liveWeather.onPrev}
              onNext={liveWeather.onNext}
              prevLabel={liveWeather.prevLabel ?? 'Previous'}
              nextLabel={liveWeather.nextLabel ?? 'Next'}
            />
          )}
          {/* The arrows arrive with a fade except when the stack's first paint draws the stored state or motion is reduced. */}
          {liveStorm?.onPrev && liveStorm.onNext && (
            <TierArrows
              fadeIn={settled && !reducedMotion}
              centerY={centerY('lower')}
              onPrev={liveStorm.onPrev}
              onNext={liveStorm.onNext}
              prevLabel={liveStorm.prevLabel ?? 'Previous'}
              nextLabel={liveStorm.nextLabel ?? 'Next'}
            />
          )}
        </Box>
      </Box>
    </Box>
    {impact && (
      // The entrance is laid out in the page's flow, in the stack's design pixel (--impact-px), over the stack's canvas. The reach
      // tiles size from --reach-px, which on the desktop stays v28's size beside the stack instead of growing with it. The
      // frame sets --plate-overhang when a tall plate (a phone's) reaches above its own clearance; the overlay pads clear of it,
      // and 'safe center' starts it at the top when it is taller than the room, so its upper rows are never pushed out of sight.
      // It is centred in the host, which is how it was built against a horizon at 0.6 of the viewport plus 50px; the stack stands
      // on the sea's line, so the transform moves the entrance by the difference and it keeps its place against the stack.
      <Box
        data-testid="impact-entrance"
        style={{ '--impact-px': impactPx(frameSc), '--reach-px': phone ? impactPx(reachSc) : `${desktopReachUnit(reachSc)}px` } as CSSProperties}
        sx={{ position: 'absolute', inset: 0, zIndex: 1, pointerEvents: 'none', transform: `translateY(${line - (0.6 * frameH + 50)}px)`, containerType: 'inline-size', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'safe center', p: 3, pb: 'max(24px, var(--plate-overhang, 0px))', textAlign: 'center' }}
      >
        {/* The footprint is the stack's own height; the screen's tiles are centred on it. */}
        <Box data-testid="impact-stack-footprint" sx={{ position: 'relative', width: '100%' }}>
        {/* The hidden clouds give the rows the height the entrance is laid out against; the stack's own tiers are drawn on the canvas. */}
        <Box sx={{ position: 'relative', width: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
          <Box data-testid="impact-weather-row" aria-hidden sx={{ visibility: 'hidden' }}>
            <Box sx={{ display: 'flex', justifyContent: 'center' }}><WeatherBlob weather={weather} /></Box>
          </Box>
        </Box>

        {/* One box around the lower three tiers, always present, so the rain can fill exactly their height. */}
        <Box data-testid="stack-lower" aria-hidden sx={{ position: 'relative', width: '100%' }}>
          <Box sx={{ visibility: 'hidden' }}>
            <Box sx={{ mt: -2, position: 'relative', zIndex: 1, width: '100%', display: 'flex', justifyContent: 'center' }}>
              <RevealBlob heading={reveal?.heading} text={reveal?.text} />
            </Box>
            <Box sx={{ mt: -0.5, width: '100%', display: 'flex', justifyContent: 'center' }}><StormBlob storm={storm} /></Box>
            <Box sx={{ mt: -2, position: 'relative', zIndex: 1, width: '100%', display: 'flex', justifyContent: 'center' }}>
              <FeelingBlob feeling={feeling} />
            </Box>
          </Box>
          {/* The rain falls from the weather response, so it waits for the content and stays away when no weather is stored. */}
          {weather && stormy && <ImpactRain span={crownSpan} still={still} dissolving={dissolve.fading} />}
        </Box>

        {/* The screen's controls, centred on the stack's footprint. */}
        {cloudTiers?.tiles}
        </Box>
      </Box>
    )}
    </>
  );
}
